import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDocs,
  updateDoc,
} from "firebase/firestore";
import { deleteObject, ref } from "firebase/storage";
import { db, storage } from "../firebase";
import { useAuth } from "../contexts/AuthContext";

const getUserDisplayName = (user) =>
  user?.name || user?.email || user?.id || "Unknown user";

const formatEventDate = (date) =>
  date?.toDate?.().toLocaleDateString("en-GB") || "No date";

const getUploadPath = (ownerId, eventId, imageName) =>
  `users/${ownerId}/events/${eventId}/uploads/${imageName}`;

const AdminEventManagerPage = () => {
  const [users, setUsers] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [images, setImages] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedItems, setSelectedItems] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [contentError, setContentError] = useState("");
  const { isSiteAdmin } = useAuth();

  useEffect(() => {
    const loadOverview = async () => {
      if (!isSiteAdmin) return;

      setLoading(true);
      setError("");

      try {
        const [usersResult, eventsResult] = await Promise.allSettled([
          getDocs(collection(db, "users")),
          getDocs(collectionGroup(db, "events")),
        ]);

        if (usersResult.status === "rejected") {
          throw usersResult.reason;
        }

        const eventList =
          eventsResult.status === "fulfilled"
            ? eventsResult.value.docs
                .map((eventDoc) => {
                  const ownerId = eventDoc.ref.path.split("/")[1];
                  return { id: eventDoc.id, ownerId, ...eventDoc.data() };
                })
                .sort((a, b) => (b.date?.seconds || 0) - (a.date?.seconds || 0))
            : [];
        const userIdsWithEvents = new Set(eventList.map((event) => event.ownerId));

        const userList = usersResult.value.docs.map((userDoc) => {
          const data = userDoc.data();
          return {
            id: userDoc.id,
            name: `${data.firstname || ""} ${data.surname || ""}`.trim(),
            email: data.email,
            role: data.role,
            hasEvents: userIdsWithEvents.has(userDoc.id),
          };
        });

        setUsers(userList);
        setAllEvents(eventList);
        if (eventsResult.status === "rejected") {
          console.error("Failed to load all events:", eventsResult.reason);
          setError("Users loaded, but all-events access is blocked.");
        }
      } catch (err) {
        console.error("Failed to load manager overview:", err);
        setError("Could not load users. Check Firestore rules.");
      } finally {
        setLoading(false);
      }
    };

    loadOverview();
  }, [isSiteAdmin]);

  useEffect(() => {
    if (!selectedUserId) {
      setEvents([]);
      return;
    }

    const loadEvents = async () => {
      const snap = await getDocs(collection(db, "users", selectedUserId, "events"));
      const data = snap.docs
        .map((eventDoc) => ({ id: eventDoc.id, ...eventDoc.data() }))
        .sort((a, b) => (b.date?.seconds || 0) - (a.date?.seconds || 0));
      setEvents(data);
    };

    loadEvents();
  }, [selectedUserId]);

  useEffect(() => {
    if (!selectedUserId || !selectedEventId) {
      setImages([]);
      setMessages([]);
      setSelectedItems({});
      setContentError("");
      return;
    }

    const loadContent = async () => {
      setContentError("");

      try {
        const msgSnap = await getDocs(
          collection(
            db,
            "users",
            selectedUserId,
            "events",
            selectedEventId,
            "messages"
          )
        );
        const msgData = msgSnap.docs.map((messageDoc) => ({
          id: messageDoc.id,
          ...messageDoc.data(),
        }));

        const imageData = msgData
          .filter((message) => message.imageUrl && message.imageName)
          .map((message) => ({
            path: getUploadPath(
              selectedUserId,
              selectedEventId,
              message.imageName
            ),
            name: message.imageName,
            url: message.imageUrl,
          }));

        setMessages(msgData);
        setImages(imageData);
      } catch (err) {
        console.error("Failed to load messages:", err);
        setMessages([]);
        setImages([]);
        setContentError("Could not load messages for this event.");
      }

      setSelectedItems({});
    };

    loadContent();
  }, [selectedUserId, selectedEventId]);

  const selectedUser = users.find((user) => user.id === selectedUserId);
  const selectedEvent = events.find((event) => event.id === selectedEventId);
  const reviewMessages = messages.filter(
    (message) => message.moderationStatus === "needsReview"
  );

  const filteredUsers = useMemo(
    () =>
      users.filter((user) => {
        const userEvents = allEvents.filter((event) => event.ownerId === user.id);
        const haystack = [
          user.name,
          user.email,
          ...userEvents.flatMap((event) => [
            event.title,
            event.shortCode,
            event.customSlug,
          ]),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(search.toLowerCase());
      }),
    [allEvents, search, users]
  );
  const eventCountsByUser = useMemo(
    () =>
      allEvents.reduce((counts, event) => {
        counts[event.ownerId] = (counts[event.ownerId] || 0) + 1;
        return counts;
      }, {}),
    [allEvents]
  );

  if (!isSiteAdmin) {
    return <div className="p-6 text-center text-red-500">Access Denied</div>;
  }

  const handleRoleChange = async (userId, newRole) => {
    try {
      await updateDoc(doc(db, "users", userId), { role: newRole });
      setUsers((prev) =>
        prev.map((user) =>
          user.id === userId ? { ...user, role: newRole } : user
        )
      );
    } catch (err) {
      console.error("Failed to update role:", err);
      alert("Failed to update role.");
    }
  };

  const handleSelectAll = (checked) => {
    const newSelected = {};
    if (checked) {
      images.forEach((img) => {
        newSelected[img.name] = true;
      });
      messages.forEach((msg) => {
        if (msg.imageName) newSelected[msg.imageName] = true;
      });
    }
    setSelectedItems(checked ? newSelected : {});
  };

  const handleDeleteSelected = async () => {
    for (const img of images) {
      if (selectedItems[img.name]) {
        await deleteObject(ref(storage, img.path));
      }
    }

    for (const msg of messages) {
      if (msg.imageName && selectedItems[msg.imageName]) {
        await deleteDoc(
          doc(
            db,
            "users",
            selectedUserId,
            "events",
            selectedEventId,
            "messages",
            msg.id
          )
        );
      }
    }

    setImages((prev) => prev.filter((img) => !selectedItems[img.name]));
    setMessages((prev) =>
      prev.filter((msg) => !msg.imageName || !selectedItems[msg.imageName])
    );
    setSelectedItems({});
  };

  const approveMessage = async (messageId) => {
    await updateDoc(
      doc(
        db,
        "users",
        selectedUserId,
        "events",
        selectedEventId,
        "messages",
        messageId
      ),
      {
        moderationStatus: "approved",
        moderationReason: "",
      }
    );

    setMessages((prev) =>
      prev.map((message) =>
        message.id === messageId
          ? { ...message, moderationStatus: "approved", moderationReason: "" }
          : message
      )
    );
  };

  const deleteMessageAndImage = async (message) => {
    if (message.imageName) {
      const image = images.find((img) => img.name === message.imageName);
      if (image) {
        await deleteObject(ref(storage, image.path));
        setImages((prev) => prev.filter((img) => img.name !== image.name));
      }
    }

    await deleteDoc(
      doc(
        db,
        "users",
        selectedUserId,
        "events",
        selectedEventId,
        "messages",
        message.id
      )
    );

    setMessages((prev) => prev.filter((item) => item.id !== message.id));
  };

  const usersWithEvents = users.filter((user) => user.hasEvents).length;
  const selectedCount = Object.keys(selectedItems).length;

  return (
    <div className="min-h-screen bg-gray-100 pb-16">
      <div className="border-b bg-white px-4 py-5 shadow-sm">
        <div className="mx-auto max-w-7xl">
          <h1 className="text-2xl font-bold text-gray-900">
            Developer Manager
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            View registered organisers, events, uploads, messages, and review
            queues across the site.
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <div className="rounded bg-pink-50 p-3">
              <strong className="text-xl text-pink-700">{users.length}</strong>
              <span className="block text-sm text-gray-600">
                Registered users
              </span>
            </div>
            <div className="rounded bg-purple-50 p-3">
              <strong className="text-xl text-purple-700">
                {allEvents.length}
              </strong>
              <span className="block text-sm text-gray-600">Total events</span>
            </div>
            <div className="rounded bg-green-50 p-3">
              <strong className="text-xl text-green-700">
                {usersWithEvents}
              </strong>
              <span className="block text-sm text-gray-600">
                Users with events
              </span>
            </div>
          </div>
          {loading && <p className="mt-3 text-sm text-gray-500">Loading...</p>}
          {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row">
        <div className="w-full border-b bg-white p-4 lg:w-1/4 lg:border-r">
          <input
            type="text"
            placeholder="Search users or events..."
            className="mb-4 w-full rounded border p-2"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <div>
            <h2 className="mb-2 text-sm font-bold uppercase tracking-wide text-gray-500">
              Users
            </h2>
            {filteredUsers.map((user) => (
              <div
                key={user.id}
                className={`border-b p-2 ${
                  user.id === selectedUserId ? "bg-pink-100 font-bold" : ""
                }`}
              >
                <button
                  type="button"
                  onClick={() => {
                    setSelectedUserId(user.id);
                    setSelectedEventId(null);
                  }}
                  className="w-full text-left"
                >
                  <div className="flex items-center justify-between gap-2 font-semibold">
                    <span>{getUserDisplayName(user)}</span>
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                      {eventCountsByUser[user.id] || 0}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">{user.email}</div>
                </button>

                <div className="mt-2 flex items-center gap-2">
                  <select
                    value={user.role || "user"}
                    onChange={(e) => handleRoleChange(user.id, e.target.value)}
                    className="rounded border px-2 py-1 text-sm"
                  >
                    <option value="user">User</option>
                    <option value="siteAdmin">Site Admin</option>
                  </select>
                </div>
              </div>
            ))}
            {filteredUsers.length === 0 && (
              <p className="rounded bg-white p-3 text-sm text-gray-500">
                No users match your search.
              </p>
            )}
          </div>
        </div>

        <div className="w-full border-b bg-white p-4 lg:w-1/3 lg:border-r">
          <h2 className="mb-2 text-lg font-bold">Events</h2>
          {selectedUser && (
            <p className="mb-3 text-sm text-gray-600">
              Showing events for {getUserDisplayName(selectedUser)}
            </p>
          )}
          {events.map((event) => (
            <button
              key={event.id}
              type="button"
              onClick={() => setSelectedEventId(event.id)}
              className={`block w-full rounded p-2 text-left ${
                event.id === selectedEventId ? "bg-purple-100 font-bold" : ""
              }`}
            >
              {event.title || "Untitled"} - {formatEventDate(event.date)}
            </button>
          ))}
          {selectedUserId && events.length === 0 && (
            <p className="text-sm text-gray-500">This user has no events.</p>
          )}
          {!selectedUserId && (
            <p className="text-sm text-gray-500">
              Select a user or choose an event from the all-events list.
            </p>
          )}
        </div>

        <div className="w-full p-4 lg:w-2/5">
          <div className="mb-2 flex items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-bold">Uploads & Messages</h2>
              {selectedEvent && (
                <p className="text-sm text-gray-600">
                  {selectedEvent.title || "Untitled"} -{" "}
                  {formatEventDate(selectedEvent.date)}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={handleDeleteSelected}
              className="rounded bg-red-500 px-3 py-1 text-white disabled:opacity-50"
              disabled={selectedCount === 0}
            >
              Delete Selected
            </button>
          </div>

          <div className="mb-2">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                className="mr-2"
                checked={images.length > 0 && selectedCount === images.length}
                onChange={(e) => handleSelectAll(e.target.checked)}
              />
              Select All
            </label>
          </div>

          {!selectedEventId && (
            <p className="rounded bg-white p-4 text-sm text-gray-500 shadow-sm">
              Select an event to inspect uploads and guest messages.
            </p>
          )}

          {contentError && (
            <p className="mb-4 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              {contentError}
            </p>
          )}

          {reviewMessages.length > 0 && (
            <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <h3 className="mb-3 font-bold text-amber-900">Needs Review</h3>
              <div className="space-y-3">
                {reviewMessages.map((message) => {
                  const image = images.find(
                    (img) => img.name === message.imageName
                  );

                  return (
                    <div key={message.id} className="rounded bg-white p-3 shadow-sm">
                      {image && (
                          <img
                            src={image.url}
                            alt={image.name}
                            className="mb-2 h-32 w-full rounded object-cover"
                          />
                      )}
                      <p className="text-sm italic text-gray-700">
                        "{message.message}"
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        - {message.name || "Anonymous"} -{" "}
                        {message.moderationReason || "needs review"}
                      </p>
                      <div className="mt-3 flex gap-2">
                        <button
                          type="button"
                          onClick={() => approveMessage(message.id)}
                          className="rounded bg-green-600 px-3 py-1 text-sm font-semibold text-white"
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteMessageAndImage(message)}
                          className="rounded bg-red-600 px-3 py-1 text-sm font-semibold text-white"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {selectedEventId && (
            <div className="space-y-6">
              <section>
                <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-gray-500">
                  Messages
                </h3>
                {messages.length > 0 ? (
                  <div className="space-y-3">
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className="rounded bg-white p-3 shadow-sm"
                      >
                        <p className="text-sm italic text-gray-700">
                          "{message.message}"
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          - {message.name || "Anonymous"}
                        </p>
                        {message.moderationStatus === "needsReview" && (
                          <p className="mt-2 text-xs font-semibold text-amber-700">
                            Needs review:{" "}
                            {message.moderationReason || "moderation"}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="rounded bg-white p-4 text-sm text-gray-500 shadow-sm">
                    No messages for this event yet.
                  </p>
                )}
              </section>

              <section>
                <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-gray-500">
                  Uploads
                </h3>
                {images.length > 0 ? (
                  <div className="grid grid-cols-2 gap-4">
                    {images.map((img) => {
                      const message = messages.find(
                        (m) => m.imageName === img.name
                      );

                      return (
                        <div
                          key={img.name}
                          className="relative rounded border bg-white p-2"
                        >
                          <img
                            src={img.url}
                            alt={img.name}
                            className="h-40 w-full rounded object-cover"
                          />
                          <div className="mt-2 flex items-center">
                            <input
                              type="checkbox"
                              checked={!!selectedItems[img.name]}
                              onChange={(e) =>
                                setSelectedItems((prev) => ({
                                  ...prev,
                                  [img.name]: e.target.checked,
                                }))
                              }
                              className="mr-2"
                            />
                            <span className="truncate text-xs">{img.name}</span>
                          </div>
                          {message?.message && (
                            <p className="mt-1 p-2 text-xs italic text-gray-600">
                              "{message.message}"
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="rounded bg-white p-4 text-sm text-gray-500 shadow-sm">
                    No uploads for this event yet.
                  </p>
                )}
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminEventManagerPage;
